import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PageConsensusBanner from '../components/PageConsensusBanner';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, CaretUp, CaretDown } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const cls = status === 'HOT' ? 'bg-red-100 text-red-800' : status === 'WARM' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800';
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`} data-testid={`status-${status.toLowerCase()}`}>{status}</span>;
};

const MomentumBadge = ({ label }) => {
  if (label === 'HEATING') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800">HEATING</span>;
  if (label === 'COOLING') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">COOLING</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500">FLAT</span>;
};

const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };
const bkeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
const bShort = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5' };

function ProfileLockedCard({ plData }) {
  if (!plData) return null;
  const bks = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const profileBg = (s) => {
    const k = `${s.status}+${s.pos_mom}+${s.xb_mom}`;
    // Distinct color per dominant profile so the UI is scan-friendly
    if (k.startsWith('WARM+COOLING+COOLING')) return 'bg-amber-100 text-amber-900 border-amber-300';
    if (k.startsWith('COLD+FLAT+FLAT'))       return 'bg-sky-100 text-sky-900 border-sky-300';
    if (k.startsWith('HOT+HEATING+HEATING'))  return 'bg-rose-100 text-rose-900 border-rose-300';
    if (k.startsWith('COLD+FLAT'))            return 'bg-indigo-100 text-indigo-900 border-indigo-300';
    return 'bg-slate-100 text-slate-900 border-slate-300';
  };
  return (
    <section className="bg-white border-2 border-amber-400 rounded-xl overflow-hidden shadow-sm" data-testid="w11-profile-locked-card">
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-5 py-3 text-white">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Star size={20} weight="fill" className="text-amber-200" />
            <h3 className="font-extrabold text-base sm:text-lg">Profile-Locked Top-5</h3>
            <span className="text-[10px] uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded font-bold">Strategy v1</span>
          </div>
          <div className="text-[10px] text-white/85 font-mono">
            Filter by Status + POS-MOM + XB-MOM profile, sort by lowest pred_score
          </div>
        </div>
        <p className="text-[11px] text-white/85 mt-1">
          B1 &amp; B5 lock to <b>WARM+COOLING+COOLING</b> · B2/B3/B4 lock to <b>COLD+FLAT+FLAT</b> · fallback profiles fill if &lt;5 matches.
          <span className="ml-2 text-amber-100">Backtested ≈ 20.5% top-5 hit rate (vs 14% default).</span>
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-amber-200">
        {bks.map((bk, bi) => {
          const block = plData[bk] || {};
          const pp = block.primary_profile || {};
          const picks = block.picks || [];
          const profileLabel = pp.status ? `${pp.status}+${pp.pos_mom}+${pp.xb_mom}` : '—';
          return (
            <div key={bk} className="px-4 py-3" data-testid={`w11-profile-locked-${bk}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-extrabold text-slate-800 text-sm">Ball {bi + 1}</span>
                <span className="text-[9px] text-slate-400 uppercase tracking-wider font-bold">primary</span>
              </div>
              <div className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded border ${profileBg(pp)} mb-2`}>
                {profileLabel}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid={`w11-profile-locked-${bk}-picks`}>
                {picks.length === 0 ? (
                  <span className="text-[10px] text-slate-400 italic">No matches</span>
                ) : picks.map((p, idx) => {
                  const isPrimary = p.profile_rank === 1;
                  const profileClass = profileBg({ status: p.status, pos_mom: p.momentum_label, xb_mom: p.xb_momentum_label });
                  return (
                    <div key={`${p.number}-${idx}`} className="flex flex-col items-center" data-testid={`w11-profile-locked-${bk}-pick-${p.number}`}>
                      <span className={`w-9 h-9 rounded-full flex items-center justify-center font-mono font-extrabold text-sm ${isPrimary ? 'bg-amber-500 text-white ring-2 ring-amber-300' : 'bg-white text-slate-800 ring-2 ring-slate-300'} border border-slate-200`} title={`${p.profile_label} · slot ${p.profile_rank} · score ${p.score}`}>
                        {p.number}
                      </span>
                      <span className="text-[9px] font-mono text-slate-500 mt-0.5">{Number(p.score).toFixed(2)}</span>
                      {!isPrimary && (
                        <span className={`mt-0.5 text-[8px] font-bold px-1 rounded border ${profileClass}`} title={p.profile_label}>
                          slot {p.profile_rank}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {block.shortfall > 0 && (
                <p className="text-[9px] text-rose-600 font-bold mt-2">⚠ {block.shortfall} short — no more matching profiles</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function Window11PositionalHotCold() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [btData, setBtData] = useState(null);
  const [mainTab, setMainTab] = useState('analysis');
  const [sortConfig, setSortConfig] = useState({ key: 'pred_score', dir: 'desc' });

  useEffect(() => {
    (async () => {
      try {
        const [res, btRes] = await Promise.all([
          axios.get(`${API}/analysis/positional-hot-cold`),
          axios.get(`${API}/analysis/w11-hotcold-backtest`),
        ]);
        setData(res.data);
        setBtData(btRes.data);
      } catch { toast.error('Failed to fetch'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 11</h1><p className="text-xs text-gray-500">Positional Hot/Cold Analysis</p></div>
            </div>
          </div>
          <ExportButton data={data} filename="w11_hot_cold" />
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <PageConsensusBanner className="mb-4" />
        <WindowNarrative windowId="W11" {...NARRATIVES.W11} className="mb-5" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <div className="space-y-4">
            {/* ⭐ Profile-Locked Top-5 — User-defined strategy.
                Per-ball Status+POS-MOM+XB-MOM profile hierarchy, lowest score first.
                Backtested at ~20% top-5 hit rate (vs ~14% for the default top-5 by score). */}
            <ProfileLockedCard plData={data.profile_locked_top5} />

            {/* Main Tab Switcher */}
            <div className="flex gap-2 bg-white border border-gray-200 rounded-lg p-1.5" data-testid="main-tabs">
              <button onClick={() => setMainTab('analysis')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'analysis' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-analysis">
                Positional Analysis
              </button>
              <button onClick={() => setMainTab('backtest')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'backtest' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-backtest">
                2026 Hot/Cold Backtest
              </button>
            </div>

            {/* Analysis Tab */}
            {mainTab === 'analysis' && (
              <AnalysisTab data={data} sortConfig={sortConfig} handleSort={handleSort} SortIcon={SortIcon} />
            )}

            {/* Backtest Tab */}
            {mainTab === 'backtest' && (
              <HotColdBacktest data={btData} hotColdData={data} />
            )}
          </div>
        ) : null}
      </main>
    </div>
  );
}

function AnalysisTab({ data, sortConfig, handleSort, SortIcon }) {
  return (
    <Tabs defaultValue="ball_1" className="space-y-4">
      <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
        {Object.entries(ballLabels).map(([k, l]) => (
          <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid={`tab-${k}`}>{l}</TabsTrigger>
        ))}
      </TabsList>
      {Object.entries(ballLabels).map(([key, label]) => {
        const nums = data.balls?.[key] || [];
        const hot = nums.filter(n => n.status === 'HOT').length;
        const warm = nums.filter(n => n.status === 'WARM').length;
        const cold = nums.filter(n => n.status === 'COLD').length;
        const sorted = [...nums].sort((a, b) => {
          let aV = a[sortConfig.key] || 0, bV = b[sortConfig.key] || 0;
          return sortConfig.dir === 'desc' ? bV - aV : aV - bV;
        });
        const topPredicted = new Set([...nums].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0)).slice(0, 5).map(n => n.number));
        return (
          <TabsContent key={key} value={key}>
            <div className="flex gap-3 mb-4 text-xs font-bold flex-wrap">
              <span className="px-2 py-1 bg-red-100 text-red-800 rounded">HOT (last 10): {hot}</span>
              <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded">WARM (last 20): {warm}</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">COLD: {cold}</span>
            </div>
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              <div className="overflow-x-auto" data-testid="hot-cold-table">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      {[['number','#'],['times','Times'],['overall_pct','%'],['current_gap','Gap'],['avg_gap','Avg Gap'],['overdue_ratio','Overdue'],['streak','Streak'],['status','Status'],['cy_this','CY Pos'],['cy_other','CY Other'],['cross_pressure','Pressure'],['h1','1st10'],['h2','2nd10'],['momentum_label','Pos Mom'],['xb_h1','XB 1st'],['xb_h2','XB 2nd'],['xb_momentum_label','XB Mom'],['pred_score','Score']].map(([k,l]) => (
                        <th key={k} className="px-2 py-2 text-[10px] uppercase text-gray-700 cursor-pointer hover:bg-gray-200 text-center whitespace-nowrap" onClick={() => handleSort(k)}>{l} <SortIcon k={k} /></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((n, idx) => (
                      <tr key={n.number} className={topPredicted.has(n.number) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={topPredicted.has(n.number) ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}} data-testid={`row-${n.number}`}>
                        <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                        <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.times}</td>
                        <td className="px-2 py-1 border-b font-mono text-center text-[10px]">{(n.overall_pct * 100).toFixed(1)}%</td>
                        <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.current_gap > n.avg_gap ? 'text-red-600' : 'text-green-700'}`}>{n.current_gap}</td>
                        <td className="px-2 py-1 border-b font-mono text-center text-[10px]">{n.avg_gap}</td>
                        <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.overdue_ratio > 1.5 ? 'text-red-600' : n.overdue_ratio > 1 ? 'text-orange-600' : 'text-gray-500'}`}>{n.overdue_ratio}x</td>
                        <td className={`px-2 py-1 border-b font-mono text-center ${n.streak > 0 ? 'font-bold text-green-700' : 'text-gray-300'}`}>{n.streak || '-'}</td>
                        <td className="px-2 py-1 border-b text-center"><StatusBadge status={n.status} /></td>
                        <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.cy_this > 0 ? 'text-green-600' : 'text-gray-300'}`}>{n.cy_this || '-'}</td>
                        <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.cy_other > 0 ? 'text-orange-600' : 'text-gray-300'}`}>{n.cy_other || '-'}</td>
                        <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.cross_pressure >= 3 ? 'text-red-600 bg-red-50' : n.cross_pressure > 0 ? 'text-orange-600 bg-orange-50' : 'text-gray-300'}`}>{n.cross_pressure > 0 ? n.cross_pressure : '-'}</td>
                        <td className={`px-2 py-1 border-b font-mono text-center ${n.h1 > n.h2 ? 'text-red-500' : n.h1 < n.h2 ? 'text-green-600' : 'text-gray-400'}`}>{n.h1}</td>
                        <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.h2 > n.h1 ? 'text-green-600' : n.h2 < n.h1 ? 'text-red-500' : 'text-gray-400'}`}>{n.h2}</td>
                        <td className="px-2 py-1 border-b text-center"><MomentumBadge label={n.momentum_label} /></td>
                        <td className={`px-2 py-1 border-b font-mono text-center ${n.xb_h1 > n.xb_h2 ? 'text-red-500' : n.xb_h1 < n.xb_h2 ? 'text-green-600' : 'text-gray-400'}`}>{n.xb_h1}</td>
                        <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.xb_h2 > n.xb_h1 ? 'text-green-600' : n.xb_h2 < n.xb_h1 ? 'text-red-500' : 'text-gray-400'}`}>{n.xb_h2}</td>
                        <td className="px-2 py-1 border-b text-center"><MomentumBadge label={n.xb_momentum_label} /></td>
                        <td className="px-2 py-1 border-b font-mono text-center font-bold text-sm">{n.pred_score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Temperature Distribution Analysis - Last 10 Draws */}
            {(() => {
              const ta = data.temperature_analysis?.[key];
              if (!ta) return null;
              const catTextColors = { HOT: 'text-red-400', WARM: 'text-amber-400', COLD: 'text-blue-400' };
              return (
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-lg p-5 mt-4 text-white" data-testid={`temp-analysis-${key}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Target size={22} weight="bold" />
                    <h3 className="font-bold text-lg">Temperature Distribution — Last 10 Draws ({label})</h3>
                  </div>
                  <p className="text-xs text-white/60 mb-4">HOT = appeared in {ta.current_year} for this ball | COLD = high frequency ({'>'}2%) but NOT appeared in {ta.current_year} | WARM = low frequency, not appeared</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="text-xs text-white/70 mb-2 font-bold uppercase tracking-wider">Last 10 Distribution</div>
                      <div className="flex h-6 rounded-lg overflow-hidden mb-3">
                        {ta.hot_pct > 0 && <div className="bg-red-500 flex items-center justify-center text-[10px] font-bold" style={{ width: `${ta.hot_pct}%` }}>HOT {ta.hot_pct}%</div>}
                        {ta.warm_pct > 0 && <div className="bg-amber-500 flex items-center justify-center text-[10px] font-bold text-black" style={{ width: `${ta.warm_pct}%` }}>WARM {ta.warm_pct}%</div>}
                        {ta.cold_pct > 0 && <div className="bg-blue-500 flex items-center justify-center text-[10px] font-bold" style={{ width: `${ta.cold_pct}%` }}>COLD {ta.cold_pct}%</div>}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        {ta.predictions.map(p => (
                          <div key={p.category} className={`rounded p-2 ${p.category === 'HOT' ? 'bg-red-500/20' : p.category === 'WARM' ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
                            <div className={`font-bold text-sm ${catTextColors[p.category]}`}>{p.category}</div>
                            <div className="text-white/80">{p.last10_count}/10 draws</div>
                            <div className={`font-mono ${p.deviation > 0 ? 'text-red-300' : p.deviation < 0 ? 'text-green-300' : 'text-white/50'}`}>
                              {p.deviation > 0 ? '+' : ''}{p.deviation}% vs expected
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-xs bg-white/10 rounded-lg p-2.5 flex items-center gap-3">
                        <span className="text-white/60">Last Draw:</span>
                        <span className="font-mono font-bold text-lg">{ta.last_draw_number}</span>
                        <StatusBadge status={ta.last_draw_freq_tier} />
                        <MomentumBadge label={ta.last_draw_momentum} />
                        <span className="text-white/40 text-[10px]">{ta.current_year} count: {ta.last_draw_cy_count}x</span>
                      </div>
                      {ta.cold_numbers && ta.cold_numbers.length > 0 && (
                        <div className="mt-2 text-xs">
                          <span className="text-blue-300 font-bold">COLD (overdue): </span>
                          <span className="text-white/70">
                            {ta.cold_numbers.slice(0, 8).map(c => `#${c.number}(${c.times}x)`).join(', ')}
                          </span>
                        </div>
                      )}
                      {ta.pressure_numbers && ta.pressure_numbers.length > 0 && (
                        <div className="mt-3 bg-orange-500/15 rounded-lg p-3 border border-orange-500/30" data-testid={`pressure-section-${key}`}>
                          <div className="text-xs font-bold text-orange-300 uppercase tracking-wider mb-2">Cross-Position CY Pressure</div>
                          <p className="text-[10px] text-white/50 mb-2">Numbers active in {ta.current_year} at OTHER positions but absent HERE</p>
                          <div className="flex flex-wrap gap-1.5">
                            {ta.pressure_numbers.map(p => (
                              <div key={p.number} className="bg-orange-500/25 rounded px-2 py-1 text-[11px] font-mono border border-orange-400/30">
                                <span className="font-bold text-orange-200">#{p.number}</span>
                                <span className="text-white/50 ml-1">{p.times}x</span>
                                <span className="text-orange-300 ml-1 font-bold">{p.cy_other}x other</span>
                                {p.other_positions && p.other_positions.map(op => (
                                  <span key={op.pos} className="text-white/40 ml-0.5 text-[9px]">{op.pos.replace('ball_', 'B')}:{op.count}</span>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-white/70 mb-2 font-bold uppercase tracking-wider">Next Draw Prediction (Mean Reversion)</div>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {ta.predictions.map((p, i) => (
                          <div key={p.category} className={`rounded-lg p-3 text-center ${i === 0 ? 'bg-white/20 ring-2 ring-white/40' : 'bg-white/5'}`}>
                            <div className={`text-xs font-bold ${catTextColors[p.category]}`}>{p.category}</div>
                            <div className="text-2xl font-bold font-mono mt-1">{p.likelihood}%</div>
                            <div className="text-[10px] text-white/50 mt-1">{i === 0 ? 'MOST LIKELY' : ''}</div>
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-white/70 mb-1 font-bold uppercase tracking-wider">Draw-by-Draw (Last 10)</div>
                      <div className="flex flex-wrap gap-1">
                        {ta.draw_details.map((dd, i) => (
                          <div key={i} className={`rounded px-2 py-1 text-[10px] font-mono font-bold ${dd.freq_tier === 'HOT' ? 'bg-red-500/30 text-red-300' : dd.freq_tier === 'WARM' ? 'bg-amber-500/30 text-amber-300' : 'bg-blue-500/30 text-blue-300'}`}>
                            {dd.number} <span className="text-white/40">{dd.freq_tier[0]}</span> <span className="text-white/30">{dd.cy_count}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-4 text-white">
              <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Predicted Next {label}</h3></div>
              <p className="text-xs text-white/70 mb-4">Score = <strong>12% Freq</strong> + <strong>22% Overdue</strong> + <strong>12% Gap</strong> + <strong>8% Hot/Cold</strong> + <strong>4% Streak</strong> + <strong>12% Pos-Momentum</strong> + <strong>12% XB-Momentum</strong> + <strong>18% Cross-Position Pressure</strong>.</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[...nums].sort((a, b) => b.pred_score - a.pred_score).slice(0, 10).map((n, idx) => (
                  <div key={n.number} className={`p-3 rounded-lg ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`} data-testid={`pred-${n.number}`}>
                    <div className="text-2xl font-mono font-bold text-center">{n.number}</div>
                    <div className="text-xs text-center mt-1 opacity-80">Gap: {n.current_gap} | {n.overdue_ratio}x</div>
                    <div className="text-xs text-center mt-1 opacity-80">H1:{n.h1} H2:{n.h2} {n.momentum_label === 'HEATING' ? '>' : n.momentum_label === 'COOLING' ? '<' : '='}</div>
                    <div className="text-xs text-center opacity-60">XB: {n.xb_h1}{'->'}{n.xb_h2} {n.xb_momentum_label === 'HEATING' ? '>' : n.xb_momentum_label === 'COOLING' ? '<' : ''}</div>
                    {n.cross_pressure > 0 && <div className="text-xs text-center mt-1 font-bold text-orange-300">CP: {n.cy_other}x other pos</div>}
                    <div className="text-center font-bold mt-1">{n.pred_score}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        );
      })}
    </Tabs>
  );
}

function HotColdBacktest({ data, hotColdData }) {
  if (!data) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;

  const statusColor = (s) => s === 'HOT' ? 'bg-red-500 text-white' : s === 'WARM' ? 'bg-amber-500 text-black' : 'bg-blue-500 text-white';
  const statusLetter = (s) => s === 'HOT' ? 'H' : s === 'WARM' ? 'W' : 'C';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-rose-700 rounded-xl p-6 text-white" data-testid="w11-backtest-header">
        <h2 className="text-2xl font-bold mb-1">2026 Hot/Cold Backtest</h2>
        <p className="text-sm text-red-100">
          For each 2026 draw, what was the Hot/Cold status of the drawn number at that point?
          HOT = appeared in last 10 draws at that position | WARM = last 20 only | COLD = absent from last 20.
        </p>
        <div className="flex gap-4 mt-3 text-xs">
          <span className="bg-white/20 px-3 py-1 rounded-full">Total 2026 draws: {data.total_2026_draws}</span>
          <span className="bg-white/20 px-3 py-1 rounded-full">First Half: {data.first_half_count} draws</span>
          <span className="bg-white/20 px-3 py-1 rounded-full">Second Half: {data.second_half_count} draws</span>
        </div>
      </div>

      {/* Current Hot/Cold/Warm Numbers per Ball */}
      <div className="space-y-4" data-testid="current-hotcold-status">
        {bkeys.map(bk => {
          const cs = data.current_status?.[bk] || {};
          return (
            <div key={bk} className="bg-white border rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white px-4 py-2.5 flex items-center justify-between">
                <span className="font-bold">{ballLabels[bk]} — Current Status</span>
                <div className="flex gap-3 text-[10px]">
                  <span className="bg-red-400/30 px-2 py-0.5 rounded">HOT: {cs.hot?.length || 0}</span>
                  <span className="bg-amber-400/30 px-2 py-0.5 rounded">WARM: {cs.warm?.length || 0}</span>
                  <span className="bg-blue-400/30 px-2 py-0.5 rounded">COLD: {cs.cold?.length || 0}</span>
                </div>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-red-200 bg-red-50 p-2.5">
                  <div className="text-[10px] font-bold text-red-700 uppercase mb-2">HOT — Last 10 ({cs.hot?.length || 0})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(cs.hot || []).map(n => (
                      <span key={n} className="inline-flex w-8 h-8 rounded-full bg-red-500 text-white items-center justify-center font-mono font-bold text-xs">{n}</span>
                    ))}
                    {(!cs.hot || cs.hot.length === 0) && <span className="text-[10px] text-gray-400">None</span>}
                  </div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                  <div className="text-[10px] font-bold text-amber-700 uppercase mb-2">WARM — Last 20 only ({cs.warm?.length || 0})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(cs.warm || []).map(n => (
                      <span key={n} className="inline-flex w-8 h-8 rounded-full bg-amber-500 text-white items-center justify-center font-mono font-bold text-xs">{n}</span>
                    ))}
                    {(!cs.warm || cs.warm.length === 0) && <span className="text-[10px] text-gray-400">None</span>}
                  </div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-2.5">
                  <div className="text-[10px] font-bold text-blue-700 uppercase mb-2">COLD — Not in last 20 ({cs.cold?.length || 0})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(cs.cold || []).map(n => (
                      <span key={n} className="inline-flex w-8 h-8 rounded-full bg-blue-500 text-white items-center justify-center font-mono font-bold text-xs">{n}</span>
                    ))}
                    {(!cs.cold || cs.cold.length === 0) && <span className="text-[10px] text-gray-400">None</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* First Half vs Second Half Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-bold text-gray-900 mb-1">First Half — Draws 1-14</h3>
          <p className="text-[10px] text-gray-500 mb-3">Early 2026 — baseline distribution</p>
          <div className="overflow-x-auto" data-testid="w11-first-half-summary">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b bg-gray-50">
                  <th className="text-left py-2 px-2">Ball</th>
                  <th className="text-center py-2 px-2">HOT</th>
                  <th className="text-center py-2 px-2">WARM</th>
                  <th className="text-center py-2 px-2">COLD</th>
                </tr>
              </thead>
              <tbody>
                {bkeys.map(bk => {
                  const s = data.first_half_summary?.[bk]?.position || {};
                  return (
                    <tr key={bk} className="border-b border-gray-100">
                      <td className="py-2 px-2 font-bold text-gray-800">{ballLabels[bk]}</td>
                      <td className="py-2 px-2 text-center"><span className="font-mono font-bold text-red-600">{s.HOT?.pct || 0}%</span><span className="text-[9px] text-gray-400 ml-1">({s.HOT?.count || 0})</span></td>
                      <td className="py-2 px-2 text-center"><span className="font-mono font-bold text-amber-600">{s.WARM?.pct || 0}%</span><span className="text-[9px] text-gray-400 ml-1">({s.WARM?.count || 0})</span></td>
                      <td className="py-2 px-2 text-center"><span className="font-mono font-bold text-blue-600">{s.COLD?.pct || 0}%</span><span className="text-[9px] text-gray-400 ml-1">({s.COLD?.count || 0})</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-bold text-gray-900 mb-1">Second Half — Draws 15-28</h3>
          <p className="text-[10px] text-gray-500 mb-3">Recent 2026 — trend shift visible</p>
          <div className="overflow-x-auto" data-testid="w11-second-half-summary">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b bg-gray-50">
                  <th className="text-left py-2 px-2">Ball</th>
                  <th className="text-center py-2 px-2">HOT</th>
                  <th className="text-center py-2 px-2">WARM</th>
                  <th className="text-center py-2 px-2">COLD</th>
                </tr>
              </thead>
              <tbody>
                {bkeys.map(bk => {
                  const s = data.second_half_summary?.[bk]?.position || {};
                  const fh = data.first_half_summary?.[bk]?.position || {};
                  const hotDiff = ((s.HOT?.pct || 0) - (fh.HOT?.pct || 0)).toFixed(1);
                  const warmDiff = ((s.WARM?.pct || 0) - (fh.WARM?.pct || 0)).toFixed(1);
                  const coldDiff = ((s.COLD?.pct || 0) - (fh.COLD?.pct || 0)).toFixed(1);
                  return (
                    <tr key={bk} className="border-b border-gray-100">
                      <td className="py-2 px-2 font-bold text-gray-800">{ballLabels[bk]}</td>
                      <td className="py-2 px-2 text-center">
                        <span className="font-mono font-bold text-red-600">{s.HOT?.pct || 0}%</span>
                        <span className={`ml-1 text-[9px] font-bold ${hotDiff > 0 ? 'text-emerald-600' : hotDiff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{hotDiff > 0 ? '+' : ''}{hotDiff}</span>
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className="font-mono font-bold text-amber-600">{s.WARM?.pct || 0}%</span>
                        <span className={`ml-1 text-[9px] font-bold ${warmDiff > 0 ? 'text-emerald-600' : warmDiff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{warmDiff > 0 ? '+' : ''}{warmDiff}</span>
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className="font-mono font-bold text-blue-600">{s.COLD?.pct || 0}%</span>
                        <span className={`ml-1 text-[9px] font-bold ${coldDiff > 0 ? 'text-emerald-600' : coldDiff < 0 ? 'text-red-500' : 'text-gray-400'}`}>{coldDiff > 0 ? '+' : ''}{coldDiff}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Draw-by-draw detail */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="text-sm font-bold text-gray-700">Draw-by-Draw Hot/Cold Status (2026)</h3>
          <p className="text-[10px] text-gray-500">Each cell shows the Hot/Cold status of the drawn number at that point. Red = HOT, Amber = WARM, Blue = COLD.</p>
        </div>
        <div className="overflow-x-auto" data-testid="w11-draw-by-draw">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-2 px-3 text-[10px] uppercase text-gray-500">#</th>
                <th className="text-left py-2 px-3 text-[10px] uppercase text-gray-500">Date</th>
                {bkeys.map(bk => (
                  <th key={bk} className="text-center py-2 px-2 text-[10px] uppercase text-gray-500" colSpan={2}>{bShort[bk]}</th>
                ))}
              </tr>
              <tr className="bg-gray-50 border-b text-[9px] text-gray-400">
                <th></th><th></th>
                {bkeys.map(bk => (
                  <React.Fragment key={bk}>
                    <th className="text-center px-1">Num</th>
                    <th className="text-center px-1">Status</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.draws || []).map((dr, i) => {
                const isSecondHalf = i >= 14;
                return (
                  <tr key={i} className={`border-b border-gray-100 ${isSecondHalf ? '' : 'bg-gray-50/50'}`}>
                    <td className="py-1.5 px-3 font-bold text-gray-500">{i + 1}</td>
                    <td className="py-1.5 px-3 font-bold text-gray-700 whitespace-nowrap">
                      {dr.draw_date}
                      {i === 13 && <span className="ml-1 text-[8px] bg-amber-100 text-amber-700 px-1 rounded">END 1st HALF</span>}
                    </td>
                    {bkeys.map(bk => {
                      const bm = dr.balls?.[bk] || {};
                      return (
                        <React.Fragment key={bk}>
                          <td className="py-1.5 px-1 text-center">
                            <span className="inline-flex w-7 h-7 rounded-full bg-slate-700 text-white items-center justify-center font-mono font-bold text-[10px]">{bm.number}</span>
                          </td>
                          <td className="py-1.5 px-1 text-center">
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${statusColor(bm.position_status)}`}>
                              {statusLetter(bm.position_status)}
                            </span>
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insight */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl p-5 text-white" data-testid="w11-backtest-insight">
        <h3 className="font-bold text-lg mb-2">Key Insight</h3>
        <p className="text-sm text-emerald-100">
          In the first 14 draws of 2026, drawn numbers had a mixed Hot/Cold/Warm distribution.
          In the second half (draws 15-28), compare how <strong>HOT numbers</strong> (those that appeared in the last 10 at their position) dominate vs COLD numbers.
          Strategy: <strong>HOT numbers at their specific position have a recency edge — prioritize them when they also show strong cross-ball activity.</strong>
        </p>
      </div>
    </div>
  );
}


export function Window11Content() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [btData, setBtData] = useState(null);
  const [mainTab, setMainTab] = useState('analysis');
  const [sortConfig, setSortConfig] = useState({ key: 'pred_score', dir: 'desc' });

  useEffect(() => {
    (async () => {
      try {
        const [res, btRes] = await Promise.all([
          axios.get(`${API}/analysis/positional-hot-cold`),
          axios.get(`${API}/analysis/w11-hotcold-backtest`),
        ]);
        setData(res.data);
        setBtData(btRes.data);
      } catch { toast.error('Failed to fetch'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 bg-white border border-gray-200 rounded-lg p-1.5">
        <button onClick={() => setMainTab('analysis')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'analysis' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Positional Analysis</button>
        <button onClick={() => setMainTab('backtest')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'backtest' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>2026 Hot/Cold Backtest</button>
      </div>
      {mainTab === 'analysis' ? (
        <Tabs defaultValue="ball_1" className="space-y-4">
          <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
            {bkeys.map(bk => <TabsTrigger key={bk} value={bk} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold">{ballLabels[bk]}</TabsTrigger>)}
          </TabsList>
          {bkeys.map(bk => {
            const nums = data.balls?.[bk] || [];
            const sorted = [...nums].sort((a, b) => { let aV=a[sortConfig.key]||0, bV=b[sortConfig.key]||0; return sortConfig.dir==='desc'?bV-aV:aV-bV; });
            const topPredicted = new Set([...nums].sort((a,b)=>(b.pred_score||0)-(a.pred_score||0)).slice(0,5).map(n=>n.number));
            const hot=nums.filter(n=>n.status==='HOT').length, warm=nums.filter(n=>n.status==='WARM').length, cold=nums.filter(n=>n.status==='COLD').length;
            return (
              <TabsContent key={bk} value={bk}>
                <div className="flex gap-3 mb-3 text-xs font-bold">
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded">HOT: {hot}</span>
                  <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded">WARM: {warm}</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">COLD: {cold}</span>
                </div>
                <div className="bg-white border border-gray-200 rounded overflow-hidden overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        {[['number','#'],['status','Status'],['times','Times'],['last_10_count','L10'],['last_20_count','L20'],['gap','Gap'],['pct','%'],['pred_score','Score']].map(([k,l])=>(
                          <th key={k} className="px-2 py-2 text-[10px] uppercase text-gray-700 cursor-pointer hover:bg-gray-200 text-center" onClick={()=>handleSort(k)}>{l} <SortIcon k={k} /></th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((n,idx)=>(
                        <tr key={n.number} className={topPredicted.has(n.number)?'bg-blue-100':idx%2===0?'bg-white':'bg-gray-50'}>
                          <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                          <td className="px-2 py-1 border-b text-center"><StatusBadge status={n.status}/></td>
                          <td className="px-2 py-1 border-b font-mono text-center">{n.times}</td>
                          <td className="px-2 py-1 border-b font-mono text-center">{n.last_10_count}</td>
                          <td className="px-2 py-1 border-b font-mono text-center">{n.last_20_count}</td>
                          <td className={`px-2 py-1 border-b font-mono text-center ${n.gap>20?'text-red-600 font-bold':''}`}>{n.gap}</td>
                          <td className="px-2 py-1 border-b font-mono text-center">{n.pct}%</td>
                          <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.pred_score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-4 text-white">
                  <h3 className="font-bold mb-3">Top 10 Predicted — {ballLabels[bk]}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[...nums].sort((a,b)=>b.pred_score-a.pred_score).slice(0,10).map((n,idx)=>(
                      <div key={n.number} className={`p-3 rounded-lg ${idx===0?'bg-sky-100 text-sky-900':'bg-white/10'}`}>
                        <div className="text-2xl font-mono font-bold text-center">{n.number}</div>
                        <div className="text-xs text-center mt-1 opacity-80">{n.status} | Gap: {n.gap}</div>
                        <div className="text-center font-bold mt-1">{n.pred_score}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      ) : btData ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-bold mb-2">2026 Hot/Cold Backtest</h3>
          <p className="text-xs text-gray-500">Backtest results for W11 hot/cold predictions</p>
          <pre className="text-xs bg-gray-50 p-3 rounded mt-2 overflow-auto max-h-[400px]">{JSON.stringify(btData, null, 2)}</pre>
        </div>
      ) : null}
    </div>
  );
}
